<template>
  <div>
    <DetailCard v-if="ehrBuildingPart.length" class="mb-4">
      <template #heading>
        <span class="pi pi-building"></span>
        <a :href="getEhrLivingRoomExternalUrl" target="_blank">
          {{
            livingroom
              ? $t('messages.detail.ehrBuildingPart.livingRoom')
              : $t(`messages.detail.ehrBuildingPart.notLivingRoom`)
          }}
        </a>
      </template>
      <DataTable
        :value="ehrBuildingPart"
        row-hover
        v-model:selection="evaluationStore.selectedBuildingPart"
        data-key="ehitise-osa-number"
        @row-select="onRowSelected"
        selection-mode="single"
        size="small"
        rowGroupMode="subheader"
        groupRowsBy="ehitise-osa-korrus"
        sortMode="single"
        sortField="ehitise-osa-korrus"
        :sortOrder="1"
        :pt="{
          rowGroupHeaderCell: 'bg-surface-100 py-2'
        }"
        tableStyle="min-width: 100%"
      >
        <!-- Dynamic ColumnGroup header -->
        <ColumnGroup type="header">
          <!-- First header row -->
          <Row>
            <template v-for="col in columns.filter(col => col.key !== 'ehitise-osa-korrus')" :key="col.key || col.groupHeader">
              <!-- Group header with colspan -->
              <Column
                v-if="'groupHeader' in col"
                :header="col.groupHeader"
                :colspan="col.columns.length"
                class="text-center text-sm"
              />
              <!-- Regular column with rowspan -->
              <Column v-else :rowspan="hasGroupedColumns ? 2 : 1">
                <template #header>
                  <div class="text-sm font-bold">
                    {{ col.label }}
                    <span v-if="col.unit">(m<sup>2</sup>)</span>
                  </div>
                </template>
              </Column>
            </template>
          </Row>
          <!-- Second header row - only sub-columns of groups -->
          <Row v-if="hasGroupedColumns">
            <Column v-for="col in groupedSubColumns" :key="col.key">
              <template #header>
                <div class="text-xs font-bold">{{ col.label }}</div>
              </template>
            </Column>
          </Row>
        </ColumnGroup>

        <!-- Body columns -->
        <Column v-for="column in flatColumns" :key="column.key" :field="column.key">
          <template #body="slotProps">
            <div v-if="column.key === 'ehitise-osa-rodude-pind'" class="text-sm">
              {{ slotProps.data[column.key] === '-' ? '' : slotProps.data[column.key] }}
            </div>
            <div v-else class="text-sm">
              {{ slotProps.data[column.key] }}
            </div>
          </template>
        </Column>
        <template #groupheader="slotProps">
          <div class="flex items-center gap-2 font-bold text-sm">
            <span class="pi pi-building"></span>
            {{ slotProps.data['ehitise-osa-korrus'] }}. korrus
          </div>
        </template>
      </DataTable>
    </DetailCard>

    <DetailCard v-if="ehrBuildingPart.length" class="mb-4">
      <template #heading>
        <span>{{ $t(`messages.detail.interior.korteri_ruumilahendus`) }}</span>
      </template>
      <div class="p-4">
        <div class="sm:w-1/2 mb-4">
          <PredefinedValuesInputComponent
            category="interior"
            name="korteri_ruumilahendus"
            placeholder="Kirjelda korteri ruumilahendust..."
            :append-on-select="true"
            v-model="interior.korteri_ruumilahendus"
            @update:model-value="evaluationStore.save({ interior: { korteri_ruumilahendus: $event } })"
          />
        </div>
        <div class="sm:w-1/2 grid grid-cols-2 gap-4 mb-4">
          <div class="flex flex-col">
            <label class="font-bold">
              {{ $t('messages.detail.ehrConstruction.rodu') }}
            </label>
            <span v-if="hasBalcony" class="text-sm text-primary-500">
              {{ evaluationStore.selectedBuildingPart['ehitise-osa-rodude-pind'] }} m²
            </span>
          </div>
          <RatingMultiSelect
            v-model="ehrConstruction.rodu"
            field="rodu"
            :options="[0, 1, 2, 3]"
            :maxSelectedLabels="3"
            :selectionLimit="3"
            placeholder="Vali kuni 3"
            size="base"
            tooltipPosition="right"
            @update:model-value="evaluationStore.save({ construction: { rodu: $event } })"
          />
        </div>
      </div>
    </DetailCard>
  </div>
</template>

<script setup>
import { router } from '@inertiajs/vue3'
import { storeToRefs } from 'pinia'
import { computed, onMounted, readonly, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTransactionStore } from '@/stores/transaction'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import PredefinedValuesInputComponent from '../../PredefinedValuesInputComponent.vue'
import DetailCard from '../DetailCard.vue'
import RatingMultiSelect from '@/components/RatingMultiSelect.vue'

const store = useDetailStore()
const evaluationStore = useEvaluationStore()
const transactionStore = useTransactionStore()
const { ehrConstruction } = storeToRefs(store)

const {
  interior,
  ehrId,
  evaluationLivingRoom,
  evaluationNotLivingRoom,
  ehrLivingRoom,
  ehrNotLivingRoom,
} = storeToRefs(store)

const { t } = useI18n()

const props = defineProps({
  livingroom: { type: Boolean, default: false },
})

const hasBalcony = computed(() => {
  if (!evaluationStore.selectedBuildingPart) {
    return false
  }

  if (evaluationStore.selectedBuildingPart['ehitise-osa-rodude-pind'] && evaluationStore.selectedBuildingPart['ehitise-osa-rodude-pind'] > 0) {
    return true
  }

  return false
})

const columns = readonly([
  { key: 'ehitise-osa-number', label: t('messages.detail.ehrBuildingPart.buidingPartNo') },
  { key: 'ehitise-osa-tubade-arv', label: t('messages.transactions.ehr_fields.hooneosa_tubade_arv') },
  { key: 'ehitise-osa-korrus', label: t('messages.transactions.ehr_fields.hooneosa_sissepaasu_korrus') },
  { key: 'ehitise-osa-pind', label: t('messages.transactions.ehr_fields.hooneosa_pind'), unit: 'm²' },
  { key: 'ehitise-osa-koetav-pind', label: t('messages.transactions.ehr_fields.hooneosa_koetav_pind'), unit: 'm²' },
  { key: 'ehitise-osa-rodude-pind', label: t('messages.detail.ehrBuildingPart.ehitise-osa-rodude-pind'), unit: 'm²' },
  { key: 'ehitise-osa-kookide-arv', label: t('messages.transactions.ehr_fields.hooneosa_kookide_arv') },
  { key: 'ehitise-osa-avatud-kookide-arv', label: t('messages.transactions.ehr_fields.hooneosa_avatud_kookide_arv') },
  { key: 'ehitise-osa-aadress', label: t('messages.transactions.ehr_fields.hooneosa_aadress') },
  { key: 'ehitise-osa-tualett', label: t('messages.transactions.ehr_fields.hooneosa_tualettruumide_liik') },
  { key: 'ehitise-osa-pesemis-voimalus', label: t('messages.transactions.ehr_fields.hooneosa_pesemisvoimaluse_liik') },
  { key: 'ehitise-osa-gaasipaigaldise', label: t('messages.transactions.ehr_fields.hooneosa_gaasipaigaldis') },
  {
    groupHeader: 'Soojus',
    columns: [
      { key: 'ehitise-osa-soojavarustuse-liik', label: t('messages.transactions.ehr_fields.hooneosa_soojavarustuse_liik') },
      { key: 'ehitise-osa-soojusallikas', label: t('messages.transactions.ehr_fields.hooneosa_soojusallika_liik') },
    ],
  },
  { key: 'ehitise-osa-energia-liik', label: t('messages.transactions.ehr_fields.hooneosa_energiaallika_liik') },
  { key: 'ehitise-osa-nimetus', label: t('messages.transactions.ehr_fields.hooneosa_nimetus') },
  { key: 'ehitise-osa-kasutamis-otstarve', label: t('messages.transactions.ehr_fields.hooneosa_kasutusotstarve') },
])

// Check if we have any grouped columns
const hasGroupedColumns = computed(() => columns.some(col => 'groupHeader' in col))

// Flatten columns for body rendering
const flatColumns = computed(() => columns.flatMap(col => ('groupHeader' in col ? col.columns : [col])))

// Get sub-columns from groups for second header row
const groupedSubColumns = computed(() =>
  columns.filter(col => 'groupHeader' in col).flatMap(col => col.columns),
)

const getEhrLivingRoomExternalUrl = computed(() => {
  return 'https://ehr.ee/app/link/b/' + ehrId.value + '/ehos'
})

const ehrBuildingPart = computed(() => {
  if (props.livingroom) {
    return ehrLivingRoom.value
  }
  return ehrNotLivingRoom.value
})

const onRowSelected = item => {
  if (props.livingroom) {
    evaluationLivingRoom.value = item.data
  } else {
    evaluationNotLivingRoom.value = item.data
  }

  try {
    evaluationStore.selectedBuildingPart = item.data

    if (evaluationStore.prediction?.similar_properties?.length > 0) {
      evaluationStore.predict().then(prediction => {
        if (prediction?.similar_properties?.length > 0) {
          evaluationStore.updatePrediction(prediction).then(() => {
            nextTick(() => {
              transactionStore.setComparisonPage(1)

              evaluationStore.save({
                comparison_table_state: {
                  comparisonComponentPage: 1,
                },
              })
            })
          })
        }
      })
    }
  } catch (error) {
    console.log(error)
  }
}
</script>
